/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Site-wide i18n (Chairman multi-language directive 2026-07-19).
 * - String-keyed (gettext style): t('Browse the catalog') looks up the English
 *   string in the active language dictionary; missing key -> English. A page
 *   can never break or show a raw key.
 * - Dictionaries lazy-load per language (dynamic import) — EN costs nothing.
 * - First visit: cookie/localStorage choice wins; else CF country via
 *   /api/geo; else navigator.language; else EN. Manual switcher persists and
 *   always overrides auto-detection afterwards.
 */

export type Lang = 'en' | 'ro' | 'mk' | 'sr' | 'bg' | 'da'
export const LANGS: { code: Lang; native: string; flag: string }[] = [
  { code: 'en', native: 'English', flag: 'gb' },
  { code: 'ro', native: 'Română', flag: 'ro' },
  { code: 'mk', native: 'Македонски', flag: 'mk' },
  { code: 'sr', native: 'Srpski', flag: 'rs' },
  { code: 'bg', native: 'Български', flag: 'bg' },
  { code: 'da', native: 'Dansk', flag: 'dk' },
]

const COUNTRY_TO_LANG: Record<string, Lang> = {
  RO: 'ro', MD: 'ro',
  MK: 'mk',
  RS: 'sr', BA: 'sr', ME: 'sr',
  BG: 'bg',
  DK: 'da',
}

const STORE_KEY = 'qordo_lang'

type Dict = Record<string, string>
const loadedDicts: Partial<Record<Lang, Dict>> = { en: {} }

async function loadDict(lang: Lang): Promise<Dict> {
  if (lang === 'en') return {}
  if (loadedDicts[lang]) return loadedDicts[lang]!
  try {
    const mod = (await import(`../i18n/${lang}.ts`)) as { default: Dict }
    loadedDicts[lang] = mod.default
    return mod.default
  } catch {
    return {}
  }
}

function storedLang(): Lang | null {
  try {
    const v = localStorage.getItem(STORE_KEY)
    return LANGS.some((l) => l.code === v) ? (v as Lang) : null
  } catch {
    return null
  }
}

function browserLang(): Lang | null {
  const nav = (navigator.language || '').slice(0, 2).toLowerCase()
  return LANGS.some((l) => l.code === nav) ? (nav as Lang) : null
}

async function geoLang(): Promise<Lang | null> {
  try {
    const r = await fetch('/api/geo')
    if (!r.ok) return null
    const j = (await r.json()) as { country?: string }
    return COUNTRY_TO_LANG[j.country ?? ''] ?? null
  } catch {
    return null
  }
}

type Ctx = { lang: Lang; setLang: (l: Lang) => void; t: (s: string) => string }
const I18nSiteContext = createContext<Ctx>({ lang: 'en', setLang: () => {}, t: (s) => s })

export function useI18n() {
  return useContext(I18nSiteContext)
}

export function I18nSiteProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => storedLang() ?? 'en')
  const [dict, setDict] = useState<Dict>({})
  const [autoDone, setAutoDone] = useState(!!storedLang())

  // Auto-detect once, only when the visitor never chose manually.
  useEffect(() => {
    if (autoDone) return
    let alive = true
    ;(async () => {
      const detected = (await geoLang()) ?? browserLang()
      if (alive && detected) setLangState(detected)
      if (alive) setAutoDone(true)
    })()
    return () => {
      alive = false
    }
  }, [autoDone])

  useEffect(() => {
    let alive = true
    loadDict(lang).then((d) => {
      if (alive) setDict(d)
    })
    document.documentElement.lang = lang
    return () => {
      alive = false
    }
  }, [lang])

  const value = useMemo<Ctx>(
    () => ({
      lang,
      setLang: (l: Lang) => {
        setLangState(l)
        try {
          localStorage.setItem(STORE_KEY, l)
          document.cookie = `${STORE_KEY}=${l};path=/;max-age=31536000;SameSite=Lax`
        } catch {
          /* private mode */
        }
      },
      t: (s: string) => dict[s] ?? s,
    }),
    [lang, dict],
  )

  return <I18nSiteContext.Provider value={value}>{children}</I18nSiteContext.Provider>
}
